// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import { QueryKey } from "@tanstack/react-query";
import { InferResult, sql } from "kysely";

import { executeSqlV2, queryBuilder } from "~/api/materialize";

/**
 * The most recent utilization sample per replica, from the same `_3h` view the
 * cluster detail charts read, so both surfaces show the same number.
 *
 * NOTE: fractions, not percentages. The hour bound leaves a replica with no
 * recent sample blank rather than hours stale.
 */
export function buildReplicaUtilizationQuery({
  includeSwap = false,
}: {
  /**
   * Select `swapPercent`, swap's share of the heap limit as the cluster page's
   * memory chart stacks it, or null for a size whose heap limit is only its
   * RAM. Its columns need mz >= 26.44.
   */
  includeSwap?: boolean;
} = {}) {
  return queryBuilder
    .selectFrom("mz_console_cluster_utilization_overview_3h")
    .distinctOn("replica_id")
    .where(sql<boolean>`mz_now() <= occurred_at + INTERVAL '1 hour'`)
    .select([
      "replica_id as replicaId",
      "cpu_percent as cpuPercent",
      "memory_percent as memoryPercent",
      "disk_percent as diskPercent",
      "heap_percent as heapPercent",
    ])
    .$if(includeSwap, (qb) =>
      qb.select(
        sql<number | null>`CASE WHEN heap_limit_percent > 1
          THEN swap_of_ram_percent / heap_limit_percent END`.as("swapPercent"),
      ),
    )
    .orderBy("replica_id")
    .orderBy("occurred_at", "desc");
}

export type ReplicaUtilization = InferResult<
  ReturnType<typeof buildReplicaUtilizationQuery>
>[0];

/** Fetches the latest utilization sample for every replica in the environment. */
export async function fetchReplicaUtilization({
  params,
  queryKey,
  requestOptions,
}: {
  params: Parameters<typeof buildReplicaUtilizationQuery>[0];
  queryKey: QueryKey;
  requestOptions?: RequestInit;
}) {
  const compiledQuery = buildReplicaUtilizationQuery(params).compile();
  return executeSqlV2({ queries: compiledQuery, queryKey, requestOptions });
}
